import { Fragment, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import {
  AlertTriangle, ArrowRight, BarChart3, Boxes, CheckCircle2, ChevronDown, ClipboardList, Clock, Eye, EyeOff, FileText, IndianRupee, Package, RefreshCw,
  Receipt, ShoppingBag, Store, TrendingUp, Truck, Wallet,
  type LucideIcon,
} from '@/components/icons';
import { fetchDashboardVersion } from '@/api/dashboard';
import { getDashboardSummary, type SummaryRow, type SummarySection } from '@/api/managementSummary';
import { KPI_TEXT_SHADOW } from '@/components/ui/KpiCard';
import { PeriodPicker, usePeriod } from '@/components/ui/PeriodPicker';
import { SectionHeader } from '@/components/ui/SectionHeader';
import { useAuth } from '@/auth/useAuth';
import { apiError } from '@/lib/apiError';
import { formatINR } from '@/lib/format';
import { humanize, parseNote, splitTitle } from '@/lib/summaryText';
import { AGE_COLORS, BarList, CAT, Columns, Donut, TrendLines, Waffle, short } from './SummaryCharts';

// The main Dashboard is the Daily Management Summary (the 8 AM email) on screen:
// key figures, then sections A–F with the email's rows. Rows "for the month" follow
// the period picker; FY rows (1 April → end of last month) and point-in-time rows
// (pending, open, outstanding, stock) stay as they are.

const rupees = (n: number) => `₹${formatINR(Math.round(n))}`;
/** ₹1.18 Cr / ₹25.11 L / ₹45.2K — short form for the key figures. */
const compact = (n: number) => {
  const a = Math.abs(n);
  if (a >= 1e7) return `₹${(n / 1e7).toFixed(2)} Cr`;
  if (a >= 1e5) return `₹${(n / 1e5).toFixed(2)} L`;
  if (a >= 1e3) return `₹${(n / 1e3).toFixed(1)}K`;
  return rupees(n);
};
const greeting = () => {
  const h = new Date().getHours();
  return h < 12 ? 'Good Morning' : h < 17 ? 'Good Afternoon' : 'Good Evening';
};

// ERP tile palette — the same hues the old dashboard tiles used.
const C = { accent: '#FF8C00', blue: '#3B82F6', green: '#10B981', violet: '#8B5CF6', amber: '#F59E0B', red: '#EF4444', slate: '#64748B', pink: '#EC4899' };

/** Where each section opens, its icon, and who may see it (none = everyone with the dashboard). */
const SECTION_META: Record<string, { icon: LucideIcon; to: string; link: string; perm?: string }> = {
  sales: { icon: Receipt, to: '/sales/sp-invoice', link: 'Invoices' },
  purchase: { icon: ClipboardList, to: '/purchase/po', link: 'Purchase Orders' },
  outstanding: { icon: Wallet, to: '/accounts/customer-os', link: 'Customer Outstanding' },
  stock: { icon: Boxes, to: '/inventory/wh-ageing', link: 'Stock Ageing' },
  sis: { icon: Store, to: '/inventory/sis-dashboard', link: 'SIS Overview' },
  ecommerce: { icon: ShoppingBag, to: '/sales/online-orders', link: 'Online Orders', perm: 'online-orders' },
};

/** Hide-values toggle, remembered in this browser per dashboard card (screen only). */
function useMask(id: string): [boolean, () => void] {
  const key = `kpi.masked./dashboard.${id}`;
  const [masked, setMasked] = useState(() => { try { return localStorage.getItem(key) === '1'; } catch { return false; } });
  const toggle = () => setMasked((m) => {
    try { if (m) localStorage.removeItem(key); else localStorage.setItem(key, '1'); } catch { /* storage blocked */ }
    return !m;
  });
  return [masked, toggle];
}

function MaskButton({ masked, onToggle, label, light }: { masked: boolean; onToggle: () => void; label: string; light?: boolean }) {
  const Icon = masked ? EyeOff : Eye;
  return (
    <button
      type="button"
      onClick={(e) => { e.preventDefault(); e.stopPropagation(); onToggle(); }}
      title={masked ? 'Show values' : 'Hide values'}
      aria-label={masked ? `Show ${label}` : `Hide ${label}`}
      aria-pressed={masked}
      className={clsx(
        'rounded-md p-1 transition focus-visible:opacity-100',
        light ? 'text-white/80 hover:bg-white/20 hover:text-white' : 'text-[var(--text-muted)] hover:bg-black/[0.06] hover:text-[var(--text-primary)]',
        masked ? 'opacity-100' : 'opacity-60 sm:opacity-0 sm:group-hover:opacity-100',
      )}
    >
      <Icon size={14} />
    </button>
  );
}

const XXX = <span className="select-none tracking-widest">xxxxx</span>;

type Tone = 'critical' | 'warning' | 'good';

/** Key-figure tile in the ERP style: solid accent, faded corner icon, white figures, optional status pill. */
function Kpi({ id, label, value, amount, sub, accent, Icon, to, hint, status }: {
  id: string; label: string; value: string; amount?: string; sub?: string; accent: string; Icon: LucideIcon; to: string; hint?: string;
  status?: { tone: Tone; text: string };
}) {
  const [masked, toggle] = useMask(id);
  const StatusIcon = status?.tone === 'good' ? CheckCircle2 : status?.tone === 'warning' ? Clock : AlertTriangle;
  return (
    <Link
      to={to}
      title={hint}
      className="group relative flex min-w-0 flex-col overflow-hidden rounded-xl p-4 text-white shadow-sm transition hover:-translate-y-0.5 hover:shadow-lg focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
      style={{ background: accent, textShadow: KPI_TEXT_SHADOW }}
    >
      <Icon className="absolute -bottom-3 -right-3 h-20 w-20" style={{ color: '#fff', opacity: 0.18 }} strokeWidth={1.25} />
      <div className="absolute right-2 top-2 z-10 flex items-center gap-1">
        <MaskButton masked={masked} onToggle={toggle} label={label} light />
        <ArrowRight className="hidden h-3.5 w-3.5 text-white opacity-0 transition group-hover:opacity-80 sm:block" />
      </div>
      <div className="relative flex flex-1 flex-col">
        <span className="block truncate pr-6 text-[10px] font-extrabold uppercase tracking-wider text-white/90 sm:pr-12 sm:tracking-widest" title={label}>{label}</span>
        <div className="mt-1.5 flex flex-wrap items-baseline gap-x-2">
          <span className="text-2xl font-extrabold leading-tight tabular-nums">{masked ? XXX : value}</span>
          {amount && !masked && <span className="text-sm font-bold tabular-nums text-white/90">{amount}</span>}
        </div>
        {sub && <div className="mt-0.5 truncate text-[10px] font-semibold text-white/85" title={sub}>{masked ? 'Hidden' : sub}</div>}
        {status && !masked && (
          <div className="mt-auto pt-2">
            <span className="inline-flex max-w-full items-center gap-1 rounded-full bg-white/20 px-2 py-0.5 text-[10px] font-bold" style={{ textShadow: 'none' }}>
              <StatusIcon size={11} className="shrink-0" /><span className="truncate">{status.text}</span>
            </span>
          </div>
        )}
      </div>
    </Link>
  );
}

/** ERP dashboard card: orange icon + bold title, muted sub-line, optional "Open →" link and extra controls. */
function Card({ title, sub, icon: Icon, to, linkLabel, aside, className = '', children }: {
  title: ReactNode; sub?: ReactNode; icon: LucideIcon; to?: string; linkLabel?: string; aside?: ReactNode; className?: string; children: ReactNode;
}) {
  return (
    <div className={clsx('group flex min-w-0 flex-col rounded-2xl border border-[var(--border-color)] bg-[var(--card-bg)] p-4 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg sm:p-5', className)}>
      <div className="mb-3 flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex items-center gap-2 text-sm font-extrabold text-[var(--text-primary)]"><Icon size={15} className="shrink-0 text-accent" /> {title}</div>
          {sub && <div className="mt-0.5 text-[10px] text-[var(--text-muted)]">{sub}</div>}
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {aside}
          {to && <Link to={to} className="whitespace-nowrap text-[11px] font-semibold text-accent hover:underline">{linkLabel ?? 'Open'} →</Link>}
        </div>
      </div>
      {children}
    </div>
  );
}

type Attention = { key: string; tone: Tone; title: string; detail: string; owner: string; to: string };
/** What needs action today, each with its owner — the dashboard's first read. */
function AttentionStrip({ items }: { items: Attention[] }) {
  if (!items.length) {
    return (
      <div className="flex items-center gap-2 rounded-2xl border border-[var(--border-color)] bg-[var(--card-bg)] px-4 py-2 text-[12px] font-semibold text-green-700 dark:text-green-400">
        <CheckCircle2 size={14} /> Nothing needs attention — invoices, dispatches and collections are up to date.
      </div>
    );
  }
  // One row on wide screens: the label on the left, then evenly sized items —
  // icon · title over detail · owner pill — each shaded by its tone.
  return (
    <div className="flex flex-col gap-2 rounded-2xl border border-[var(--border-color)] bg-[var(--card-bg)] p-2.5 xl:flex-row xl:items-stretch">
      {/* Icon on the left; "Needs attention" with the open count directly beneath it. */}
      <div className="flex shrink-0 items-center gap-2 px-1.5 xl:w-[156px] xl:border-r xl:border-[var(--border-color)] xl:pr-3">
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-accent text-white shadow-sm">
          <AlertTriangle size={18} strokeWidth={2.4} />
        </span>
        <div className="min-w-0 leading-tight">
          <div className="whitespace-nowrap text-[12.5px] font-extrabold text-[var(--text-primary)]">Needs attention</div>
          <div className="mt-0.5 text-[10.5px] font-semibold text-[var(--text-muted)]">{items.length} open</div>
        </div>
      </div>
      <div className="grid flex-1 grid-cols-1 gap-2 sm:grid-cols-2 xl:grid-cols-4">
        {items.map((it) => {
          const critical = it.tone === 'critical';
          return (
            <Link key={it.key} to={it.to} title={`${it.title} — ${it.detail} · Owner: ${it.owner}`}
              className={clsx('flex min-w-0 items-center gap-2.5 rounded-xl border bg-gradient-to-br px-3 py-2 transition hover:-translate-y-0.5 hover:shadow-md',
                // Shaded by tone: red for overdue, amber for to-do.
                critical ? 'border-danger/25 from-danger/[0.12] to-danger/[0.03] hover:border-danger/50' : 'border-warning/30 from-warning/[0.14] to-warning/[0.03] hover:border-warning/60')}>
              <span className={clsx('flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-white shadow-sm', critical ? 'bg-danger' : 'bg-warning')}>
                {critical ? <AlertTriangle size={15} /> : <Clock size={15} />}
              </span>
              <div className="min-w-0 flex-1">
                <div className="truncate text-[12px] font-extrabold leading-snug text-[var(--text-primary)]">{it.title}</div>
                <div className="truncate text-[10.5px] leading-snug text-[var(--text-secondary)]">{it.detail}</div>
              </div>
              <span className={clsx('shrink-0 rounded-full px-2 py-0.5 text-[9.5px] font-bold uppercase tracking-wide',
                critical ? 'bg-danger/[0.12] text-red-700 dark:text-red-400' : 'bg-warning/[0.16] text-amber-700 dark:text-amber-400')}>
                {it.owner}
              </span>
            </Link>
          );
        })}
      </div>
    </div>
  );
}

/** A chart-only dashboard card with its own hide-values eye. */
function ChartCard({ id, title, sub, icon, children }: { id: string; title: string; sub?: string; icon: LucideIcon; children: (masked: boolean) => ReactNode }) {
  const [masked, toggle] = useMask(id);
  return (
    <Card title={title} sub={sub} icon={icon} aside={<MaskButton masked={masked} onToggle={toggle} label={`${title} values`} />}>
      {children(masked)}
    </Card>
  );
}

/** The five ageing rows ("< 30 days" … "> 90 days") of Outstanding and Stock. */
const AGE_ROW = /^(<|>|\d+ to \d+) /;

type Stat = { label: string; value: string; sub?: string };

/**
 * One report section: letter badge + title, 2–3 headline numbers, its chart, and the
 * report's exact rows behind "Show details" (opened state remembered in this browser).
 */
function SectionCard({ section, chart, stats }: { section: SummarySection; chart?: (masked: boolean) => ReactNode; stats?: Stat[] }) {
  const meta = SECTION_META[section.key];
  const { letter, name } = splitTitle(section.title);
  const [masked, toggle] = useMask(`section.${section.key}`);
  const [open, toggleOpen] = useMask(`details.${section.key}`);
  const hasCount = section.rows.some((r) => r.count);
  const Icon = meta?.icon ?? FileText;
  return (
    <Card
      className="h-full"
      title={name}
      sub={letter ? `Section ${letter} · Daily management summary` : 'Daily management summary'}
      icon={Icon}
      to={meta?.to}
      linkLabel={meta?.link}
      aside={<MaskButton masked={masked} onToggle={toggle} label={`${name} values`} />}
    >

      {stats && stats.length > 0 && (
        <div className="mb-4 grid grid-cols-3 divide-x divide-[var(--border-color)] rounded-xl bg-[var(--bg-secondary)] py-2.5">
          {stats.map((s) => (
            <div key={s.label} className="min-w-0 px-3">
              <div className="truncate text-[9.5px] font-bold uppercase tracking-wider text-[var(--text-muted)]" title={s.label}>{s.label}</div>
              <div className="mt-0.5 truncate text-[15px] font-extrabold tabular-nums text-[var(--text-primary)] sm:text-base">{masked ? XXX : s.value}</div>
              {s.sub && <div className="truncate text-[10px] text-[var(--text-secondary)]" title={s.sub}>{s.sub}</div>}
            </div>
          ))}
        </div>
      )}

      {chart && <div className="flex flex-1 flex-col justify-center">{chart(masked)}</div>}

      <button
        type="button"
        onClick={toggleOpen}
        aria-expanded={open}
        className="mt-3 flex w-full items-center justify-center gap-1.5 rounded-lg border border-dashed border-[var(--border-color)] py-1.5 text-[11px] font-semibold text-[var(--text-secondary)] transition hover:border-accent/50 hover:text-accent"
      >
        <ChevronDown size={13} className={clsx('transition-transform', open && 'rotate-180')} />
        {open ? 'Hide details' : `Show details · ${section.rows.length} rows`}
      </button>
      {open && (
        <div className="mt-3">
          {/* Same header treatment as the ERP's tables: orange uppercase on a light accent band. */}
          <div className={clsx('grid gap-x-3 rounded-md bg-accent/[0.08] px-2 py-1.5 text-[10px] font-bold uppercase tracking-wider text-accent',
            hasCount ? 'grid-cols-[minmax(0,1fr)_auto] sm:grid-cols-[minmax(0,1fr)_minmax(90px,auto)_auto]' : 'grid-cols-[minmax(0,1fr)_auto]')}>
            <span>Particulars</span>
            {hasCount && <span className="hidden sm:block">Count</span>}
            <span className="text-right">Value</span>
          </div>
          {section.rows.map((r) => (
            <Fragment key={r.label}>
              {r.gap && <div className="h-2 border-b border-[var(--text-muted)]/40" />}
              <Row row={r} hasCount={hasCount} masked={masked} />
            </Fragment>
          ))}
        </div>
      )}
    </Card>
  );
}

function Row({ row, hasCount, masked }: { row: SummaryRow; hasCount: boolean; masked: boolean }) {
  const { owner, text } = parseNote(row.note);
  const count = row.count ? humanize(row.count) : '';
  const isNum = typeof row.value === 'number';
  return (
    <div className={clsx('grid items-start gap-x-3 border-b border-[var(--border-color)] px-2 py-2 transition-colors hover:bg-accent/[0.04]',
      hasCount ? 'grid-cols-[minmax(0,1fr)_auto] sm:grid-cols-[minmax(0,1fr)_minmax(90px,auto)_auto]' : 'grid-cols-[minmax(0,1fr)_auto]',
      row.strong && 'bg-[var(--bg-secondary)]')}>
      <div className="min-w-0">
        <div className={clsx('text-[12.5px] leading-snug text-[var(--text-primary)]', row.strong && 'font-bold')}>{humanize(row.label)}</div>
        {count && <div className="mt-0.5 text-[11px] text-[var(--text-secondary)] sm:hidden">{count}</div>}
        {owner && <div className="mt-0.5 text-[10.5px] font-semibold text-accent">Owner: {owner}</div>}
        {text && <div className="mt-0.5 text-[10.5px] text-[var(--text-muted)]">{text}</div>}
      </div>
      {hasCount && <div className="hidden text-[11.5px] leading-5 text-[var(--text-secondary)] sm:block">{count}</div>}
      <div className={clsx('whitespace-nowrap text-right text-[12.5px] font-bold tabular-nums',
        row.value === 0 ? 'text-[var(--text-muted)]' : 'text-[var(--text-primary)]')}>
        {row.value == null || row.value === '' ? '' : masked && isNum ? XXX : isNum ? rupees(row.value as number) : humanize(String(row.value))}
      </div>
    </div>
  );
}

export function DashboardPage() {
  const { user, hasPerm } = useAuth();
  const period = usePeriod('month');
  const range = period.range;
  const P = range?.label ?? 'All time';

  const q = useQuery({
    queryKey: ['dashboard-summary', range?.from ?? 'all', range?.to ?? '', P],
    queryFn: () => getDashboardSummary(range ? { from: range.from, to: range.to, label: range.label } : null),
    placeholderData: keepPreviousData,
    refetchInterval: 120_000,
  });
  const d = q.data;

  // Reload only when data is saved somewhere (same live-update signal as before).
  const qc = useQueryClient();
  const refreshAll = () => { void qc.invalidateQueries({ queryKey: ['dashboard-summary'] }); };
  const versionQ = useQuery({ queryKey: ['dashboard-version'], queryFn: fetchDashboardVersion, refetchInterval: 5_000, staleTime: 0 });
  const seenVersion = useRef<number | null>(null);
  useEffect(() => {
    const v = versionQ.data;
    if (v == null) return;
    if (seenVersion.current != null && v !== seenVersion.current) refreshAll();
    seenVersion.current = v;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [versionQ.data]);

  const firstName = user?.fullName?.split(' ')[0] ?? user?.username ?? 'there';
  const sec = (key: string) => d?.sections.find((s) => s.key === key);
  const row = (key: string, re: RegExp) => sec(key)?.rows.find((r) => re.test(r.label));
  const val = (r?: SummaryRow) => (typeof r?.value === 'number' ? r.value : 0);
  const cnt = (r?: SummaryRow) => (r?.count ? humanize(r.count) : '');

  const salesP = row('sales', /INVOICE VALUE FOR/i);
  const toRaise = row('sales', /pending TO RAISE/i);
  const doPending = row('sales', /pending FOR GRN OUT/i);
  const soBal = row('sales', /SO \(Sales Orders\) BALANCE/i);
  const openPo = row('purchase', /OPEN PO/i);
  const stockRow = row('stock', /^Total stock in hand/i);
  const sisSaleP = sec('sis')?.rows.filter((r) => /^TOTAL SALE FOR/i.test(r.label))[0];
  const firstNum = (s: string) => s.split(' ')[0];

  const sections = (d?.sections ?? []).filter((s) => !SECTION_META[s.key]?.perm || hasPerm(SECTION_META[s.key].perm!));
  const canEcom = hasPerm('online-orders');

  // Line charts: the 12-month trend, without the empty months before the data starts (min 6 shown).
  const trend = useMemo(() => {
    const t = d?.trend ?? [];
    const first = t.findIndex((m) => m.sales || m.purchase || m.grn || m.sis || m.online);
    return t.slice(first < 0 ? 0 : Math.min(first, Math.max(0, t.length - 6)));
  }, [d?.trend]);

  /** "1,234 PCS" → 1234; "146 PCS ; 129 ORDERS" with /ORDER/ → 129. */
  const numIn = (r: SummaryRow | undefined, re = /^([\d,]+)/) => Number((r?.count?.match(re)?.[1] ?? '0').replace(/,/g, ''));
  const ORDERS = /([\d,]+) ORDER/i;
  const ageData = (key: string, unit?: string) => (sec(key)?.rows ?? []).filter((r) => AGE_ROW.test(r.label))
    .map((r) => ({ name: humanize(r.label), value: val(r), extra: unit && r.count ? humanize(r.count) : undefined }));

  /** The chart drawn above each section's table (the table stays as the exact figures). */
  const charts: Record<string, (masked: boolean) => ReactNode> = {
    sales: (masked) => (
      <BarList masked={masked} color={CAT.blue} data={[
        { name: 'Sales order balance', value: val(soBal), extra: cnt(soBal) },
        { name: 'DOs pending dispatch', value: val(doPending), extra: cnt(doPending) },
        { name: 'Invoices to raise', value: val(toRaise), extra: cnt(toRaise) },
        { name: `Invoiced · ${P}`, value: val(salesP), extra: cnt(salesP) },
      ]} />
    ),
    purchase: (masked) => {
      const r = (re: RegExp) => row('purchase', re);
      return (
        <BarList masked={masked} color={CAT.amber} data={[
          { name: 'POs raised · FY', value: val(r(/^TOTAL FY/i)), extra: cnt(r(/^TOTAL FY/i)) },
          { name: `POs raised · ${P}`, value: val(r(/POs raised IN/i)), extra: cnt(r(/POs raised IN/i)) },
          { name: `GRN in · ${P}`, value: val(r(/POs received/i)), extra: cnt(r(/POs received/i)) },
          { name: 'Open POs', value: val(openPo), extra: cnt(openPo) },
        ]} />
      );
    },
    outstanding: (masked) => (
      <Donut masked={masked} centre={short(d?.outstanding.total ?? 0)} centreSub="Outstanding"
        data={ageData('outstanding').map((x, i) => ({ ...x, color: AGE_COLORS[i] }))} />
    ),
    // Stock: value by age only — the category split is its own card (StockCategoryCard).
    stock: (masked) => (
      <div>
        <div className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-[var(--text-muted)]">Value by age</div>
        <Columns masked={masked} height={220} colors={AGE_COLORS} data={ageData('stock', 'pcs').map((x) => ({ ...x, name: x.name.replace(' to ', ' – ') }))} />
      </div>
    ),
    // SIS: the FY money flow as vertical steps — invoiced → sold → commission — each with a
    // bar showing its share of what was invoiced. Monthly store sales is its own card.
    sis: (masked) => {
      const r = (re: RegExp) => row('sis', re);
      const invoiced = val(r(/INVOICES RAISED FROM GHPL/i));
      const flow = [
        { label: 'Invoiced to stores', value: invoiced, extra: cnt(r(/INVOICES RAISED FROM GHPL/i)) },
        { label: 'Sold in stores', value: val(r(/TOTAL FY SALE FROM STORE/i)), extra: cnt(r(/TOTAL FY SALE FROM STORE/i)) },
        { label: 'Store commission', value: val(r(/COMMISSION/i)), extra: undefined as string | undefined },
      ];
      return (
        <div>
          <div className="mb-1.5 text-[10px] font-semibold uppercase tracking-wider text-[var(--text-muted)]">FY money flow · {d?.fyLabel}</div>
          {/* Compact steps joined by a slim connector line on the left. */}
          <div className="relative space-y-1.5 pl-3">
            <span className="absolute bottom-3 left-[5px] top-3 w-px bg-[#8B5CF6]/30" aria-hidden />
            {flow.map((f) => (
              <div key={f.label} className="relative rounded-lg border border-[#8B5CF6]/25 bg-[#8B5CF6]/[0.07] px-2.5 py-1.5">
                <span className="absolute -left-[11px] top-1/2 h-2 w-2 -translate-y-1/2 rounded-full bg-[#8B5CF6] ring-2 ring-[var(--card-bg)]" aria-hidden />
                <div className="flex items-baseline justify-between gap-2">
                  <span className="text-[11.5px] font-bold text-[var(--text-primary)]">{f.label}</span>
                  <span className="text-[14px] font-extrabold tabular-nums text-[var(--text-primary)]">{masked ? <span className="select-none tracking-widest">xxxxx</span> : short(f.value)}</span>
                </div>
                <div className="mt-1 h-1 overflow-hidden rounded-full bg-[#8B5CF6]/[0.12]">
                  <div className="h-full rounded-full bg-[#8B5CF6]" style={{ width: `${invoiced > 0 ? Math.min(100, (f.value / invoiced) * 100) : 0}%` }} />
                </div>
                <div className="mt-0.5 flex justify-between text-[10px] text-[var(--text-secondary)]">
                  <span>{f.extra ?? ''}</span>
                  <span>{invoiced > 0 && !masked ? `${((f.value / invoiced) * 100).toFixed(0)}% of invoiced` : ''}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      );
    },
    ecommerce: (masked) => {
      const r = (re: RegExp) => row('ecommerce', re);
      const slice = (name: string, re: RegExp, color: string) => {
        const x = r(re);
        return { name, value: numIn(x, ORDERS), color, amount: val(x), extra: `${numIn(x)} pcs` };
      };
      return (
        <Donut masked={masked} size={140} centre={String(numIn(r(/^ORDER RECEIVED FOR/i), ORDERS))} centreSub={`orders · ${P}`}
          valueText={(v) => `${formatINR(v)} order${v === 1 ? '' : 's'}`}
          data={[
            slice('Dispatched', /^DISPATCHED FOR/i, CAT.green),
            slice('On hold', /^ON HOLD FOR/i, CAT.violet),
            slice('Pending', /^PENDING DISPATCH WITH WAREHOUSE FOR/i, CAT.amber),
            slice('Cancelled', /^CANCELLED FOR/i, CAT.blue),
          ]} />
      );
    },
  };

  // Headline numbers shown above each section's chart.
  const fyRow = (key: string) => row(key, /^TOTAL FY/i);
  const over60 = d ? d.outstanding.b60_90 + d.outstanding.above90 : 0;
  const areaRow = row('stock', /CARPET \+ RUNNER/i);
  const stats: Record<string, Stat[]> = d ? {
    sales: [
      { label: 'FY invoiced', value: short(val(fyRow('sales'))), sub: cnt(fyRow('sales')) },
      { label: `Invoiced · ${P}`, value: short(val(salesP)), sub: cnt(salesP) },
      { label: `Returns · ${P}`, value: short(val(row('sales', /Sales Returns/i))), sub: cnt(row('sales', /Sales Returns/i)) },
    ],
    purchase: [
      { label: 'FY POs', value: short(val(fyRow('purchase'))), sub: cnt(fyRow('purchase')) },
      { label: 'Open POs', value: short(val(openPo)), sub: cnt(openPo) },
      { label: `GRN in · ${P}`, value: short(val(row('purchase', /POs received/i))), sub: cnt(row('purchase', /POs received/i)) },
    ],
    outstanding: [
      { label: 'Total', value: short(d.outstanding.total) },
      { label: 'Over 60 days', value: short(over60), sub: d.outstanding.total > 0 ? `${((over60 / d.outstanding.total) * 100).toFixed(1)}% of total` : undefined },
      { label: 'Over 90 days', value: short(d.outstanding.above90) },
    ],
    stock: [
      { label: 'Stock value', value: short(d.stock.totalValue), sub: cnt(stockRow) },
      { label: 'Carpets & mats', value: short(val(areaRow)), sub: (cnt(areaRow).split(',')[1] ?? '').trim() || undefined },
      { label: 'Over 90 days', value: short(d.stock.above90Value) },
    ],
    sis: [
      { label: 'Stores', value: String(d.sis.stores), sub: cnt(row('sis', /^SIS stores/i)) },
      { label: 'Stock in stores', value: short(val(row('sis', /CURRENT STOCK IN STORES/i))), sub: cnt(row('sis', /CURRENT STOCK IN STORES/i)) },
      { label: 'FY commission', value: short(val(row('sis', /COMMISSION/i))) },
    ],
    ecommerce: [
      { label: `Orders · ${P}`, value: formatINR(numIn(row('ecommerce', /^ORDER RECEIVED FOR/i), ORDERS)), sub: short(val(row('ecommerce', /^ORDER RECEIVED FOR/i))) },
      { label: `Pending · ${P}`, value: formatINR(numIn(row('ecommerce', /^PENDING DISPATCH WITH WAREHOUSE FOR/i), ORDERS)), sub: short(val(row('ecommerce', /^PENDING DISPATCH WITH WAREHOUSE FOR/i))) },
      { label: 'FY sale', value: short(val(fyRow('ecommerce'))), sub: cnt(fyRow('ecommerce')) },
    ],
  } : {};

  // What needs action today — only the items that are actually non-zero.
  const attention: Attention[] = !d ? [] : ([
    d.sales.invoicesPendingAfterDispatch > 0 && { key: 'invoice', tone: 'warning', title: `${formatINR(d.sales.invoicesPendingAfterDispatch)} dispatches to invoice`, detail: `${short(val(toRaise))} of goods out with no invoice`, owner: 'Accounts', to: '/sales/sp-invoice' },
    d.sales.doPendingDispatch > 0 && { key: 'dispatch', tone: 'warning', title: `${formatINR(d.sales.doPendingDispatch)} DOs to dispatch`, detail: `${short(val(doPending))} waiting for GRN out`, owner: 'Warehouse', to: '/sales/dispatch-order' },
    canEcom && d.awaitingOnline > 0 && { key: 'online', tone: 'warning', title: `${formatINR(d.awaitingOnline)} online orders waiting`, detail: 'Not yet processed into ERP orders', owner: 'Ecommerce', to: '/sales/online-orders' },
    over60 > 0 && { key: 'collect', tone: d.outstanding.above90 > 0 ? 'critical' : 'warning', title: `${short(over60)} due over 60 days`, detail: `${short(d.outstanding.above90)} of it over 90 days`, owner: 'Accounts', to: '/accounts/customer-os' },
    d.stock.above90Value > 0 && { key: 'stock', tone: 'warning', title: `${short(d.stock.above90Value)} stock over 90 days`, detail: 'Slow stock in the warehouse', owner: 'Warehouse', to: '/inventory/wh-ageing' },
  ] as (Attention | false)[]).filter((x): x is Attention => !!x);

  const refreshBtn = (pad: string) => (
    <button
      type="button" onClick={refreshAll} disabled={q.isFetching}
      className={`inline-flex items-center gap-1.5 rounded-md ${pad} text-[11px] font-semibold text-[var(--text-muted)] hover:text-accent disabled:opacity-70`}
      title="Figures update by themselves when anything is saved — click to reload now"
    >
      <RefreshCw size={12} className={q.isFetching ? 'animate-spin' : ''} />
      {q.isFetching ? 'Updating…' : q.dataUpdatedAt ? `Updated ${new Date(q.dataUpdatedAt).toLocaleTimeString('en-IN', { hour: 'numeric', minute: '2-digit' })}` : 'Refresh'}
    </button>
  );

  return (
    <div className="space-y-4">
      {/* Empty subtitle keeps the top bar to just "Dashboard"; the greeting sits on the filter row. */}
      <SectionHeader title="Dashboard" subtitle="" />
      <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2">
        <div className="min-w-0 text-[var(--text-secondary)]">
          <div className="text-lg font-extrabold leading-tight text-[var(--text-primary)] sm:inline sm:text-xl">{greeting()}, <span className="text-accent">{firstName}</span></div>
          {/* Desktop: period + FY after the greeting. Phones: on their own line with the refresh. */}
          <span className="hidden text-[15px] font-medium text-[var(--text-muted)] sm:inline"> · {P}{d ? ` · FY ${d.fyLabel}` : ''}</span>
          <div className="mt-0.5 flex flex-wrap items-center gap-x-1.5 text-xs font-medium text-[var(--text-muted)] sm:hidden">
            <span>{P}{d ? ` · FY ${d.fyLabel}` : ''}</span>
            <span aria-hidden>·</span>
            {refreshBtn('px-0 py-0')}
          </div>
        </div>
          <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:flex-wrap sm:items-center sm:justify-end">
            <span className="hidden sm:inline-flex">{refreshBtn('px-2 py-1')}</span>
            <PeriodPicker {...period.picker} stretch className="w-full justify-end sm:w-auto" />
          </div>
      </div>
      {q.isError && !d && <div className="rounded-xl border border-danger/40 bg-danger/[0.08] p-4 text-sm text-danger">{apiError(q.error, 'Could not load the dashboard')}</div>}
      {!d && q.isLoading && (
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          {Array.from({ length: 8 }, (_, i) => <div key={i} className="h-[118px] animate-pulse rounded-xl bg-black/[0.06]" />)}
        </div>
      )}

      {d && (
        <>
          {/* ── Key figures ─────────────────────────────────────── */}
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            <Kpi id="sales" label={`Sales · ${P}`} value={compact(d.sales.totalSalesMonth)} sub={`${cnt(salesP)} raised`} accent={C.green} Icon={IndianRupee} to="/sales/sp-invoice" hint="Invoices raised in the period, incl GST" />
            <Kpi id="to-raise" label="Invoices to raise" value={formatINR(d.sales.invoicesPendingAfterDispatch)} amount={compact(val(toRaise))} sub="Goods out, no invoice yet" accent={C.amber} Icon={FileText} to="/sales/sp-invoice" hint="Dispatch notes (GRN-out) with no invoice yet, valued at their dispatch order"
              status={d.sales.invoicesPendingAfterDispatch > 0 ? { tone: 'warning', text: 'Accounts to invoice' } : { tone: 'good', text: 'All invoiced' }} />
            <Kpi id="do-pending" label="DOs pending dispatch" value={formatINR(d.sales.doPendingDispatch)} amount={compact(val(doPending))} sub="No dispatch note yet" accent={C.violet} Icon={Truck} to="/sales/dispatch-order" hint="Active dispatch orders with no dispatch note (GRN-out) yet"
              status={d.sales.doPendingDispatch > 0 ? { tone: 'warning', text: 'Warehouse to dispatch' } : { tone: 'good', text: 'All dispatched' }} />
            <Kpi id="so-balance" label="Sales order balance" value={firstNum(cnt(soBal)) || '0'} amount={compact(val(soBal))} sub="Partial, on hold, under production" accent={C.blue} Icon={ClipboardList} to="/sales/enq-review" hint="Sales orders with quantity still to dispatch" />
            <Kpi id="open-po" label="Open purchase orders" value={formatINR(d.purchase.pendingPos)} amount={compact(val(openPo))} sub={`${d.purchase.posRaisedMonth} raised · ${d.purchase.posReceivedMonth} received · ${P}`} accent={C.accent} Icon={Package} to="/purchase/po" />
            <Kpi id="outstanding" label="Customer outstanding" value={compact(d.outstanding.total)} sub={`${compact(over60)} over 60 days`} accent={C.red} Icon={Wallet} to="/accounts/customer-os"
              status={d.outstanding.above90 > 0 ? { tone: 'critical', text: `${compact(d.outstanding.above90)} over 90 days` } : over60 > 0 ? { tone: 'warning', text: `${compact(over60)} over 60 days` } : { tone: 'good', text: 'Nothing over 60 days' }} />
            <Kpi id="stock" label="Stock value" value={compact(d.stock.totalValue)} sub={`${cnt(stockRow)} · at purchase rate`} accent={C.slate} Icon={Boxes} to="/inventory/wh-ageing"
              status={d.stock.above90Value > 0 ? { tone: 'warning', text: `${compact(d.stock.above90Value)} over 90 days` } : { tone: 'good', text: 'Nothing over 90 days' }} />
            <Kpi id="sis-sales" label={`SIS sales · ${P}`} value={compact(d.sis.salesMonthAmount)} sub={`${cnt(sisSaleP)} sold · ${d.sis.stores} stores`} accent={C.pink} Icon={Store} to="/inventory/sis-dashboard" hint="Amount received from customers in SIS stores, as uploaded by the stores" />
          </div>

          <AttentionStrip items={attention} />

          {/* ── Trend lines (month by month) ────────────────────── */}
          {trend.length > 0 && (
            <>
              <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
                <ChartCard id="trend-sales" title="Sales vs purchase" sub={`Invoices raised and POs raised, by month · ${trend[0].month} – ${trend[trend.length - 1].month}`} icon={TrendingUp}>
                  {(masked) => <TrendLines masked={masked} data={trend} series={[
                    { key: 'sales', label: 'Sales invoiced', color: CAT.blue },
                    { key: 'purchase', label: 'POs raised', color: CAT.amber },
                  ]} />}
                </ChartCard>
                <ChartCard id="trend-channels" title="Store & online sales" sub="SIS store sales and online orders (not cancelled), by month" icon={BarChart3}>
                  {(masked) => <TrendLines masked={masked} data={trend} series={[
                    { key: 'sis', label: 'SIS store sales', color: CAT.violet },
                    ...(canEcom ? [{ key: 'online', label: 'Online orders', color: CAT.green }] : []),
                  ]} />}
                </ChartCard>
              </div>
            </>
          )}

          {/* ── Sections A–F: headline numbers + chart; the report's rows under "Show details" ── */}
          <div className="grid grid-cols-1 gap-3 lg:grid-cols-2 xl:grid-cols-6">
            {(() => {
              const area = row('stock', /CARPET \+ RUNNER/i);
              const cards: { key: string; node: ReactNode }[] = [];
              for (const s of sections) {
                cards.push({ key: s.key, node: <SectionCard section={s} chart={charts[s.key]} stats={stats[s.key]} /> });
                // Stock by category gets its own card, right after the Stock (value by age) card.
                if (s.key === 'stock') {
                  cards.push({
                    key: 'stock-category',
                    node: (
                      <ChartCard id="stock-category" title="Stock by category" sub="Carpet, runner & doormat against everything else · at purchase rate" icon={Boxes}>
                        {(masked) => {
                          // "11,930 PCS, 1,99,337 SQ FT, 18,519 SQ MTR" → pieces, sq ft, sq m.
                          const c = area?.count ?? '';
                          const pick = (re: RegExp) => c.match(re)?.[1];
                          const sqft = pick(/([\d,]+)\s*SQ FT/i);
                          const sqm = pick(/([\d,]+)\s*SQ MTR/i);
                          const areaPcs = numIn(area);
                          const otherPcs = Math.max(0, numIn(stockRow) - areaPcs);
                          const other = Math.max(0, val(stockRow) - val(area));
                          return (
                            <>
                              {/* Headline strip, same treatment as the section cards beside it. */}
                              <div className="mb-4 grid grid-cols-3 divide-x divide-[var(--border-color)] rounded-xl bg-[var(--bg-secondary)] py-2.5">
                                {[
                                  { label: 'Carpets & mats', value: short(val(area)), sub: `${formatINR(areaPcs)} pcs` },
                                  { label: 'Everything else', value: short(other), sub: `${formatINR(otherPcs)} pcs` },
                                  { label: 'Carpet area', value: sqft ? `${sqft}` : '—', sub: sqft ? `sq ft · ${sqm ?? '—'} sq m` : undefined },
                                ].map((s) => (
                                  <div key={s.label} className="min-w-0 px-3">
                                    <div className="truncate text-[9.5px] font-bold uppercase tracking-wider text-[var(--text-muted)]" title={s.label}>{s.label}</div>
                                    <div className="mt-0.5 truncate text-[15px] font-extrabold tabular-nums text-[var(--text-primary)] sm:text-base">{masked && s.label !== 'Carpet area' ? <span className="select-none tracking-widest">xxxxx</span> : s.value}</div>
                                    {s.sub && <div className="truncate text-[10px] text-[var(--text-secondary)]" title={s.sub}>{s.sub}</div>}
                                  </div>
                                ))}
                              </div>
                              <div className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-[var(--text-muted)]">Share of stock value</div>
                              <Waffle masked={masked} data={[
                                { name: 'Carpet, runner, doormat', value: val(area), color: CAT.green, lines: [`${formatINR(areaPcs)} pcs`, ...(sqft ? [`${sqft} sq ft`] : [])] },
                                { name: 'Everything else', value: other, color: CAT.violet, lines: [`${formatINR(otherPcs)} pcs`] },
                              ]} />
                            </>
                          );
                        }}
                      </ChartCard>
                    ),
                  });
                }
                // Monthly SIS store sales gets its own card, right after Shop in Shop.
                if (s.key === 'sis') {
                  const months = trend.map((m) => ({ name: m.month, value: m.sis }));
                  cards.push({
                    key: 'sis-monthly',
                    node: (
                      <ChartCard id="sis-monthly" title="SIS store sales by month" sub={`Amount received in the stores · latest month solid${trend.length ? ` · ${trend[0].month} – ${trend[trend.length - 1].month}` : ''}`} icon={BarChart3}>
                        {(masked) => (
                          <div className="min-h-[240px] flex-1">
                            <Columns masked={masked} height="100%" data={months}
                              colors={months.map((_, i) => (i === months.length - 1 ? CAT.violet : '#C4B5FD'))} />
                          </div>
                        )}
                      </ChartCard>
                    ),
                  });
                }
              }
              // Wide screens (6-col grid): Sales · Purchase are half width; every card after them
              // sits three to a row (2 cols each). A short last row stretches its cards to fill it.
              // Medium screens (2-col grid): an odd card out takes the full width.
              const HALVES = new Set(['sales', 'purchase']);
              const thirds = cards.filter((c) => !HALVES.has(c.key));
              const tail = thirds.length % 3; // cards in the last, short row (0 = full row)
              const tailKeys = new Set(tail ? thirds.slice(-tail).map((c) => c.key) : []);
              return cards.map((c, i) => (
                <div key={c.key} className={clsx('flex min-w-0 flex-col [&>*]:flex-1',
                  HALVES.has(c.key) ? 'xl:col-span-3'
                    : tailKeys.has(c.key) ? (tail === 1 ? 'xl:col-span-6' : 'xl:col-span-3')
                      : 'xl:col-span-2',
                  cards.length % 2 === 1 && i === cards.length - 1 && 'lg:col-span-2')}>{c.node}</div>
              ));
            })()}
          </div>

          <div className="space-y-1 px-1 pb-2 text-[10.5px] leading-relaxed text-[var(--text-muted)]">
            <p><strong className="text-[var(--text-secondary)]">How these are counted.</strong> Rows for “{P}” follow the period picker; FY rows run from 1 April to the end of last month ({d.fyLabel}); pending, open, outstanding and stock rows are as of now. Outstanding is aged by invoice date (newest invoices carry the balance; opening balance counts as over 90 days). Stock is valued at purchase rate and aged by received date; SIS stock at the rate GRHAMOY invoiced the store. SIS sales are the amount received from customers as uploaded by the stores. GRN value = received qty × unit price; sales returns at MRP.</p>
            {d.awaitingOnline > 0 && canEcom && <p>E-commerce “pending dispatch” includes {d.awaitingOnline} online orders not yet processed into ERP orders.</p>}
          </div>
        </>
      )}
    </div>
  );
}
